import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Download, ExternalLink, Loader2 } from "lucide-react";
import { fileDownloadUrl } from "../../lib/files";

export default function PdfPreviewDialog({ file, open, onClose }) {
  const [loaded, setLoaded] = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    setLoaded(false);
  }, [file?.id]);

  // Global Escape listener — the iframe's PDF viewer steals focus, so the
  // Radix Dialog's built-in Escape handler never fires. Catch at window level.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!file) return null;
  const url = fileDownloadUrl(file);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="pdf-preview-dialog"
        className="max-w-6xl w-[92vw] h-[90vh] bg-zinc-950 border-zinc-800 rounded-sm p-0 overflow-hidden flex flex-col"
      >
        <DialogTitle className="sr-only">{file.file_name}</DialogTitle>
        <DialogDescription className="sr-only">
          In-page PDF preview of {file.file_name}.
        </DialogDescription>

        <div className="border-b border-zinc-800 px-4 py-2.5 flex items-center justify-between gap-3 shrink-0">
          <div className="min-w-0 flex-1">
            <div className="font-mono text-[10px] uppercase tracking-widest text-amber-400">
              .{file.file_type} · {(file.size / 1024).toFixed(1)} KB
            </div>
            <div
              className="text-sm text-zinc-100 truncate"
              title={file.file_name}
            >
              {file.file_name}
            </div>
          </div>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="pdf-preview-open-external"
            className="flex items-center gap-1.5 px-2 py-1 border border-zinc-800 hover:border-amber-400/60 hover:text-amber-400 font-mono text-[10px] uppercase tracking-widest text-zinc-300"
          >
            <ExternalLink className="h-3 w-3" /> Open
          </a>
          <a
            href={url}
            download={file.file_name}
            data-testid="pdf-preview-download"
            className="flex items-center gap-1.5 px-2 py-1 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-mono text-[10px] uppercase tracking-widest"
          >
            <Download className="h-3 w-3" /> Download
          </a>
        </div>

        <div className="relative flex-1 min-h-0 bg-zinc-900">
          {!loaded && (
            <div className="absolute inset-0 flex items-center justify-center font-mono text-xs text-zinc-500 gap-2 pointer-events-none">
              <Loader2 className="h-4 w-4 animate-spin text-amber-400" />
              Loading document…
            </div>
          )}
          <iframe
            ref={iframeRef}
            data-testid="pdf-preview-iframe"
            title={file.file_name}
            src={url}
            onLoad={() => setLoaded(true)}
            className="w-full h-full bg-zinc-900 border-0"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
